import { supabase } from '../utils/supabase';
import { Consulta, Organization } from '../types';
import { InvitationService, USER_ROLES } from './invitationService';

export interface OrganizationWithUserCount extends Organization {
  user_organizations?: Array<{ count: number }>;
}

export interface OrganizationWithConsultasCount extends Consulta {
  consultas?: Array<{ count: number }>;
}

export interface SAASCreateOrganizationData {
  name: string;
  slug?: string;
  slogan?: string;
  inviteEmail?: string;
}

export interface SAASUpdateOrganizationData {
  name: string;
  slug: string;
  slogan?: string;
}

export interface SAASOrganizationResponse {
  success: boolean;
  message: string;
  data?:
    | Organization
    | OrganizationWithUserCount
    | OrganizationWithUserCount[]
    | OrganizationWithConsultasCount
    | OrganizationWithConsultasCount[];
  error?: Error;
}

export class SAASOrganizationService {
  /**
   * Generate a URL-friendly slug from organization name
   */
  static generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Get all organizations with user count for SAAS admin view
   */
  static async getAllOrganizations(): Promise<SAASOrganizationResponse> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select(
          `
          *,
          user_organizations!inner(count),
          consultas!inner(count)
          `
        )
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Organizações carregadas com sucesso',
        data: data || []
      };
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao carregar organizações',
        error: error as Error
      };
    }
  }

  /**
   * Create a new organization with optional user invitation
   */
  static async createOrganization(
    formData: SAASCreateOrganizationData
  ): Promise<SAASOrganizationResponse> {
    if (!formData.name?.trim()) {
      return {
        success: false,
        message: 'Nome da organização é obrigatório',
        error: new Error('Nome da organização é obrigatório')
      };
    }

    try {
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert([
          {
            name: formData.name,
            slug: formData.slug || this.generateSlug(formData.name),
            slogan: formData.slogan || 'Nova organização'
          }
        ])
        .select()
        .single();

      if (orgError) {
        throw new Error(orgError.message);
      }

      let message = 'Organização criada com sucesso!';

      // Send invitation if email provided
      if (formData.inviteEmail) {
        const inviteResult = await InvitationService.inviteUser({
          email: formData.inviteEmail,
          organizationId: orgData.id,
          role: USER_ROLES.OWNER
        });

        if (inviteResult.success) {
          message += ` ${inviteResult.message}`;
        } else {
          message += ` Erro ao enviar convite para ${formData.inviteEmail}: ${inviteResult.message}`;
        }
      }

      return {
        success: true,
        message,
        data: orgData
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao criar organização: ${(error as Error).message}`,
        error: error as Error
      };
    }
  }

  /**
   * Update an existing organization
   */
  static async updateOrganization(
    organizationId: string,
    formData: SAASUpdateOrganizationData
  ): Promise<SAASOrganizationResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'ID da organização é obrigatório',
        error: new Error('ID da organização é obrigatório')
      };
    }

    if (!formData.name?.trim()) {
      return {
        success: false,
        message: 'Nome da organização é obrigatório',
        error: new Error('Nome da organização é obrigatório')
      };
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .update({
          name: formData.name,
          slug: formData.slug,
          slogan: formData.slogan
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Organização atualizada com sucesso!',
        data
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao atualizar organização: ${(error as Error).message}`,
        error: error as Error
      };
    }
  }

  /**
   * Delete an organization
   */
  static async deleteOrganization(organizationId: string): Promise<SAASOrganizationResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'ID da organização é obrigatório',
        error: new Error('ID da organização é obrigatório')
      };
    }

    try {
      const { error } = await supabase.from('organizations').delete().eq('id', organizationId);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Organização excluída com sucesso!'
      };
    } catch (error) {
      return {
        success: false,
        message: `Erro ao excluir organização: ${(error as Error).message}`,
        error: error as Error
      };
    }
  }
}
